import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Hook to get upload status information
 * @returns {object} Upload status information
 */
export function useUploadStatus() {
  const uploadStates = useSelector((state) => state.uploadProgress.uploads);
  const allAssets = useSelector((state) => state.assets);

  return useMemo(() => {
    const uploads = Object.values(uploadStates);

    // Get uploading/queued uploads
    const activeUploads = uploads.filter(upload =>
      upload.status === 'uploading' || upload.status === 'queued'
    );

    // Get failed uploads
    const failedUploads = uploads.filter(upload =>
      upload.status === 'failed'
    );

    // Get completed uploads
    const completedUploads = uploads.filter(upload =>
      upload.status === 'completed'
    );

    // Create list of uploading assets with names
    const uploadingAssets = activeUploads.map(upload => {
      // Find asset by ID across all asset types
      let assetName = 'Unknown';
      const assetTypes = ['videos', 'audios', 'images', 'documents'];

      assetTypes.some(assetType => {
        const asset = allAssets[assetType]?.find(a => a.id === upload.assetId);
        if (asset) {
          assetName = asset.name || assetName;
          return true; // break out of some()
        }
        return false;
      });

      return {
        assetId: upload.assetId,
        name: assetName,
        progress: upload.progress,
        status: upload.status,
      };
    });

    return {
      // Core status
      hasActiveUploads: activeUploads.length > 0,
      uploadingCount: activeUploads.length,
      failedCount: failedUploads.length,
      completedCount: completedUploads.length,
      totalCount: uploads.length,
      allCompleted: uploads.length > 0 && uploads.every(upload => upload.status === 'completed'),

      // Detailed info
      uploadingAssets,
      failedAssets: failedUploads.map(upload => ({
        assetId: upload.assetId,
        error: upload.error,
      })),
      completedAssets: completedUploads.map(upload => ({
        assetId: upload.assetId,
        sectionId: upload.sectionId,
      })),
    };
  }, [uploadStates, allAssets]);
}